/**
 * F-138: one sync run — the board as it is NOW, written to the folder.
 *
 *   1. build the manifest once to learn which assets it references;
 *   2. ask the host what is already on disk (`prepare`);
 *   3. download every asset it does not have and write it — the file's
 *      extension comes from the bytes' content type, which only the
 *      download reveals, so the name is decided here and remembered by
 *      being on disk (`prepare` lists it next time);
 *   4. build the manifest again with the real file names and write
 *      `storyboard.md` + `storyboard.json` last, so a reader never sees a
 *      brief that names a file not yet there.
 *
 * Pure orchestration over injected seams (host, fetch, clock) so it runs
 * under vitest with a real BoardDoc and fakes for the rest. A failed asset
 * does not fail the run: the brief still ships, naming the file it WOULD
 * be, and the result says which downloads to retry.
 */
import {
  assetExtensionFor,
  buildStoryboard,
  defaultAssetFileName,
  storyboardToMarkdown,
  type BoardDoc,
  type ShortSlot,
  type StoryboardManifest,
  type StoryboardRowBand,
  type TextKind,
} from "@kola/shared";

import type { StoryboardSyncHost } from "../../core/host.js";

export const STORYBOARD_MARKDOWN_FILE = "storyboard.md";
export const STORYBOARD_JSON_FILE = "storyboard.json";
export const STORYBOARD_ASSETS_DIR = "assets";

export interface StoryboardSyncDeps {
  board: BoardDoc;
  sheetId: string;
  boardId: string;
  boardName: string;
  host: StoryboardSyncHost;
  rootDir: string;
  kinds?: readonly TextKind[];
  rowBands?: readonly StoryboardRowBand[];
  fetchAsset(assetId: string): Promise<Blob>;
  now?: () => Date;
  /** F-142: the week's slots — a Shorts week's manifest names each short. */
  shorts?: readonly ShortSlot[];
}

export interface StoryboardSyncResult {
  ok: boolean;
  /** The board folder's absolute path, when the host reported it. */
  dir?: string;
  /** Assets downloaded and written this run. */
  written: string[];
  /** Assets that could not be fetched or written, with the reason. */
  failed: { assetId: string; error: string }[];
  /** The fatal error, when `ok` is false. */
  error?: string;
  manifest?: StoryboardManifest;
}

/**
 * The folder a board syncs into, under the root: its name made safe for
 * every file system, then its id, so two boards called "Episode 12" never
 * share a folder and a rename is still findable by id.
 */
export function storyboardFolderName(boardName: string, boardId: string): string {
  const safe = boardName
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60)
    .replace(/[. ]+$/, "");
  const id = boardId.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 24) || "board";
  return safe.length === 0 ? id : `${safe} (${id})`;
}

/** The asset's file under `assets/`, from what the bytes turned out to be. */
export function assetFileFor(assetId: string, blobType: string, hint: { contentType?: string }): string {
  let ext = assetExtensionFor(blobType);
  if (ext === "bin" && hint.contentType !== undefined) ext = assetExtensionFor(hint.contentType);
  return `${STORYBOARD_ASSETS_DIR}/${assetId}.${ext}`;
}

export async function runStoryboardSync(deps: StoryboardSyncDeps): Promise<StoryboardSyncResult> {
  const boardFolder = storyboardFolderName(deps.boardName, deps.boardId);
  const build = (assetFileName: StoryboardBuildNames): StoryboardManifest =>
    buildStoryboard(deps.board, deps.sheetId, {
      boardId: deps.boardId,
      boardName: deps.boardName,
      kinds: deps.kinds,
      rowBands: deps.rowBands,
      assetFileName,
      now: deps.now,
      shorts: deps.shorts,
    });

  const preliminary = build(defaultAssetFileName);
  const prepared = await deps.host.prepare({ rootDir: deps.rootDir, boardFolder });
  if (prepared.error !== undefined) {
    return { ok: false, written: [], failed: [], error: prepared.error };
  }
  const onDisk = new Map<string, string>(Object.entries(prepared.assets ?? {}));

  const written: string[] = [];
  const failed: { assetId: string; error: string }[] = [];
  for (const asset of preliminary.assets) {
    if (onDisk.has(asset.assetId)) continue;
    try {
      const blob = await deps.fetchAsset(asset.assetId);
      const file = assetFileFor(asset.assetId, blob.type, { contentType: asset.contentType });
      const result = await deps.host.writeAsset({
        rootDir: deps.rootDir,
        boardFolder,
        file,
        base64: await blobToBase64(blob),
      });
      if (result.error !== undefined) throw new Error(result.error);
      onDisk.set(asset.assetId, file);
      written.push(asset.assetId);
    } catch (err) {
      failed.push({ assetId: asset.assetId, error: err instanceof Error ? err.message : String(err) });
    }
  }

  const manifest = build((assetId, hint) => onDisk.get(assetId) ?? defaultAssetFileName(assetId, hint));
  const wrote = await deps.host.writeText({
    rootDir: deps.rootDir,
    boardFolder,
    files: [
      { file: STORYBOARD_MARKDOWN_FILE, text: storyboardToMarkdown(manifest) },
      { file: STORYBOARD_JSON_FILE, text: `${JSON.stringify(manifest, null, 2)}\n` },
    ],
  });
  if (wrote.error !== undefined) {
    return { ok: false, written, failed, error: wrote.error, manifest };
  }
  const result: StoryboardSyncResult = { ok: true, written, failed, manifest };
  const dir = wrote.dir ?? prepared.dir;
  if (dir !== undefined) result.dir = dir;
  return result;
}

type StoryboardBuildNames = (assetId: string, hint: { contentType?: string }) => string;

/** Base64 of a Blob's bytes, chunked so a 30 MB video never spreads one call. */
export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}
